// MatkaPlaySheet — the ONE way numbers are entered on Matka King (plan M-56/M-57/M-59).
// Three ways in: QUICK (tap digits / type a jodi or patti + amount), SHEET (every number
// on a grid with its own amount), PASTE (a WhatsApp-style list → preview). The component
// never calls the network itself: the host passes `parse` and `submit`.
//
// REBUILT 2026-09-20 on the owner's review of what annoyed players:
//   • NOTHING MOVES. The sheet is a fixed-height panel: header, chart, tabs and the entry
//     controls stay where they are, only the slip list scrolls, and the Place button is
//     pinned at the bottom. (Before, every added number grew the panel and pushed the
//     entry controls up and the Place button below the fold.)
//   • THE SLIP IS VISIBLE IN EVERY TAB with its total, split Single · Jodi · Patti. The
//     sheet tab writes straight into it — an amount typed under a number is in the slip at
//     once, highlighted, so what the footer says is always what the user typed.
//   • WHAT IS ALREADY PLACED on this session is one line with its total in every tab,
//     expandable; the sheet grid marks those numbers with a green tick. After a successful
//     Place the sheet STAYS OPEN, shows "Placed ✓ … total on this session now ₹…", and the
//     placed line refreshes — no more "did my bet go through?".
//   • THE CHART: the last six weeks (seven days for a slot game) under the game name, the
//     old panel chart, with the Panel ⇄ Jodi toggle; collapsible, remembered.
import { useEffect, useMemo, useRef, useState } from 'react';
import Icon from './Icon';
import MatkaPanelChart, { ChartModeToggle, type ChartData, type ChartMode } from './MatkaPanelChart';

export type SheetItem = { number: string; stake: number; type: 'figure' | 'jodi' | 'pana' };
export type SheetMarket = {
  id: number; kind: 'double' | 'single'; name: string;
  multipliers: { figure: number; jodi: number; panaSingle: number; panaDouble: number; panaTriple: number; commissionPct: number };
};
export type SheetRules = { min: number; max: number; maxNumbers: number; validPanas: string[] };
export type PlayedNumber = { number: string; stake: number; type: string; byAgent?: boolean };
export type PlacedInfo = { count: number; total: number };

const inr = (n: number) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
const DIGITS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'];
const panaDigit = (p: string) => String((Number(p[0]) + Number(p[1]) + Number(p[2])) % 10);
const typeOf = (n: string): SheetItem['type'] => (n.length === 1 ? 'figure' : n.length === 2 ? 'jodi' : 'pana');
const TYPE_LABEL: Record<SheetItem['type'], string> = { figure: 'Single', jodi: 'Jodi', pana: 'Patti' };
const ls = { get: (k: string) => { try { return localStorage.getItem(k); } catch { return null; } }, set: (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* ignore */ } } };

export default function MatkaPlaySheet({ market, leg, legLabel, date, deadlineText, rules, played, chart, today, parse, submit, onClose, onPlaced, forName }: {
  market: SheetMarket; leg: string; legLabel: string; date: string; deadlineText?: string; rules: SheetRules;
  played?: PlayedNumber[];
  /** undefined = no chart section; null = loading; data = render. */
  chart?: ChartData | null; today?: string;
  parse: (text: string) => Promise<{ items: SheetItem[]; rejected: Array<{ raw: string; reason: string }>; total: number }>;
  submit: (items: SheetItem[], source: 'web' | 'sheet' | 'paste') => Promise<unknown>;
  onClose: () => void; onPlaced: (placed: PlacedInfo) => void; forName?: string;
}) {
  const [mode, setMode] = useState<'quick' | 'sheet' | 'paste'>('quick');
  const [type, setType] = useState<'figure' | 'jodi' | 'pana'>('figure');
  const [num, setNum] = useState('');
  const [amt, setAmt] = useState<number>(rules.min);
  const [items, setItems] = useState<SheetItem[]>([]);
  const [pasteText, setPasteText] = useState('');
  const [preview, setPreview] = useState<{ items: SheetItem[]; rejected: Array<{ raw: string; reason: string }>; total: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [done, setDone] = useState<PlacedInfo | null>(null);
  const [showPlaced, setShowPlaced] = useState(false);
  const [chartMode, setChartMode] = useState<ChartMode>(() => (ls.get('matka.chartMode') === 'jodi' ? 'jodi' : 'panel'));
  const [chartOpen, setChartOpen] = useState<boolean>(() => ls.get('matka.chartOpen') !== '0');
  const [source, setSource] = useState<'web' | 'sheet' | 'paste'>('web');
  const numRef = useRef<HTMLInputElement>(null);
  const validPanas = useMemo(() => new Set(rules.validPanas), [rules.validPanas]);
  const panaGroups = useMemo(() => { const g: Record<string, string[]> = {}; for (const p of rules.validPanas) (g[panaDigit(p)] ||= []).push(p); return g; }, [rules.validPanas]);
  const canJodi = market.kind === 'double' && leg === 'open';
  const total = items.reduce((s, i) => s + i.stake, 0);
  const byType = useMemo(() => { const t = { figure: 0, jodi: 0, pana: 0 }; for (const i of items) t[i.type] += i.stake; return t; }, [items]);
  const placedList = played || [];
  const placedTotal = placedList.reduce((s, p) => s + p.stake, 0);
  useEffect(() => { if (type === 'jodi' && !canJodi) setType('figure'); }, [type, canJodi]);
  useEffect(() => { if (!done) return; const t = setTimeout(() => setDone(null), 7000); return () => clearTimeout(t); }, [done]);

  /** Quick / paste: ADD to the number's stake (validated now). */
  const addOne = (n: string, stake: number, t: SheetItem['type']) => {
    if (!Number.isFinite(stake) || stake < rules.min || stake > rules.max) { setMsg(`Amount must be ₹${rules.min} – ₹${rules.max}`); return false; }
    let refused = false;
    setItems((xs) => {
      const i = xs.findIndex((x) => x.number === n && x.type === t);
      if (i >= 0) { const c = [...xs]; c[i] = { ...c[i], stake: c[i].stake + stake }; return c; }
      if (xs.length >= rules.maxNumbers) { refused = true; return xs; }
      return [...xs, { number: n, stake, type: t }];
    });
    if (refused) { setMsg(`At most ${rules.maxNumbers} numbers in one slip`); return false; }
    setMsg(null); setDone(null); return true;
  };
  /** Sheet: SET the number's stake exactly (0 / empty removes it). Validated at Place. */
  const setStake = (n: string, stake: number) => {
    const t = typeOf(n);
    setItems((xs) => {
      const i = xs.findIndex((x) => x.number === n && x.type === t);
      if (!(stake > 0)) return i >= 0 ? xs.filter((_, j) => j !== i) : xs;
      if (i >= 0) { const c = [...xs]; c[i] = { ...c[i], stake }; return c; }
      if (xs.length >= rules.maxNumbers) { setMsg(`At most ${rules.maxNumbers} numbers in one slip`); return xs; }
      return [...xs, { number: n, stake, type: t }];
    });
    setSource('sheet'); setDone(null);
  };
  const addQuick = (n: string) => {
    const s = n.trim();
    const ok = type === 'figure' ? /^[0-9]$/.test(s) : type === 'jodi' ? /^[0-9]{2}$/.test(s) : validPanas.has(s);
    if (!ok) { setMsg(type === 'pana' ? `${s || '…'} is not a valid patti` : `Enter a ${type === 'figure' ? '1-digit' : '2-digit'} number`); return; }
    if (addOne(s, Math.floor(amt), type)) { setNum(''); numRef.current?.focus(); }
  };
  const doPreview = async () => { try { setPreview(await parse(pasteText)); setMsg(null); } catch (e) { setMsg(e instanceof Error ? e.message : 'Could not read the list'); } };
  const addFromPreview = () => { if (!preview) return; for (const it of preview.items) addOne(it.number, it.stake, it.type); setSource('paste'); setPreview(null); setPasteText(''); };
  const doSubmit = async () => {
    if (!items.length || busy) return;
    const bad = items.filter((i) => i.stake < rules.min || i.stake > rules.max);
    if (bad.length) { setMsg(`Amount must be ₹${rules.min} – ₹${rules.max} — fix ${bad.map((b) => b.number).join(', ')}`); return; }
    setBusy(true); setMsg(null);
    try {
      await submit(items, source);
      const placed = { count: items.length, total };
      setItems([]); setDone(placed); onPlaced(placed);
    } catch (e) { setMsg(e instanceof Error ? e.message : 'Could not place the bet'); }
    finally { setBusy(false); }
  };
  const stakeOf = (n: string) => items.find((x) => x.number === n && x.type === typeOf(n))?.stake;
  const placedOf = (n: string) => placedList.filter((p) => p.number === n).reduce((s, p) => s + p.stake, 0);
  const gridChange = (n: string, v: string) => {
    const digits = v.replace(/\D/g, '');
    if (!digits) { setStake(n, 0); return; }
    const s = Math.floor(Number(digits));
    if (s > rules.max) { setMsg(`Max ₹${rules.max} per number`); return; }
    setMsg(null); setStake(n, s);
  };
  // Plain render helper, NOT a component: a component created inside render remounts on
  // every keystroke and drops the input's focus (learned the hard way on the admin page).
  const cell = (n: string, small: boolean) => {
    const s = stakeOf(n), pl = placedOf(n);
    return (
      <label key={n} className={`rounded-lg p-0.5 text-center ${s ? 'ring-2 ring-amber-500 bg-amber-50 dark:bg-amber-500/15' : 'bg-arena-50 dark:bg-white/10'}`}>
        <div className={`${small ? 'text-[10px]' : 'text-xs'} font-black leading-tight`}>{n}</div>
        <input inputMode="numeric" value={s ?? ''} onChange={(e) => gridChange(n, e.target.value)} placeholder="₹" className="w-full text-center text-[11px] font-mono font-bold bg-transparent outline-none placeholder:text-arena-300" />
        {pl > 0 && <div className="text-[8px] font-bold text-green-700 dark:text-green-300 leading-tight">✓ {inr(pl)}</div>}
      </label>
    );
  };
  const chip = 'rounded-xl bg-amber-50 dark:bg-white/10 font-black active:scale-95';
  const tab = (on: boolean) => `py-2 rounded-xl text-xs font-bold ${on ? 'bg-amber-600 text-white' : 'bg-arena-100 dark:bg-white/10'} disabled:opacity-30`;

  return (
    <div className="fixed inset-0 z-40 flex flex-col justify-end bg-black/50" onClick={onClose}>
      <div className="flex flex-col bg-white dark:bg-[#24190b] text-arena-900 dark:text-white rounded-t-3xl h-[94dvh] overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {/* ── header: game · leg · date · type ── */}
        <div className="px-3 pt-3 pb-1.5 flex items-center gap-2 shrink-0">
          <div className="flex-1 min-w-0">
            <div className="font-black truncate">{market.name} <span className="text-amber-700 dark:text-amber-300">· {legLabel}</span>{forName ? <span className="text-xs font-bold text-amber-700 dark:text-amber-300"> · for {forName}</span> : null}</div>
            <div className="text-[11px] text-arena-500 dark:text-white/60 truncate">{date}{deadlineText ? ` · ${deadlineText}` : ''} · {market.kind === 'double' ? 'Open / Close game' : 'Time-slot game'}</div>
          </div>
          <button type="button" onClick={onClose} className="h-9 px-3 rounded-full bg-arena-100 dark:bg-white/10 text-xs font-bold active:scale-95">Done</button>
        </div>

        {/* ── the chart, right under the name (owner, 2026-09-20) ── */}
        {chart !== undefined && (
          <div className="mx-2 rounded-xl bg-arena-50 dark:bg-white/5 px-1.5 py-1 shrink-0">
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => { const v = !chartOpen; setChartOpen(v); ls.set('matka.chartOpen', v ? '1' : '0'); }} className="flex-1 flex items-center gap-1 text-[11px] font-bold text-left">
                <Icon name={chartOpen ? 'expand_less' : 'expand_more'} size={16} />{market.kind === 'double' ? 'Last 6 weeks' : 'Last 7 days'} — {chartMode === 'panel' ? 'panel chart' : 'jodi chart'}
              </button>
              {chartOpen && <ChartModeToggle mode={chartMode} onMode={(m) => { setChartMode(m); ls.set('matka.chartMode', m); }} />}
            </div>
            {chartOpen && <div className="mt-1"><MatkaPanelChart data={chart} mode={chartMode} today={today || date} weeks={6} compact /></div>}
          </div>
        )}

        {/* ── tabs ── */}
        <div className="mx-2 mt-1.5 grid grid-cols-3 gap-1 shrink-0">
          {([['quick', 'Quick'], ['sheet', 'Sheet'], ['paste', 'Paste list']] as const).map(([k, l]) => <button key={k} type="button" onClick={() => setMode(k)} className={tab(mode === k)}>{l}</button>)}
        </div>

        {/* ── already placed on this session: one line, every tab ── */}
        <div className="mx-2 mt-1.5 shrink-0">
          {placedList.length ? (
            <button type="button" onClick={() => setShowPlaced((v) => !v)} className="w-full flex items-center gap-1 rounded-lg bg-green-50 dark:bg-green-500/10 px-2 py-1 text-[11px] text-left">
              <Icon name="check_circle" size={14} className="text-green-600 dark:text-green-300" />
              <span className="flex-1">Placed on this session: <b>{placedList.length} number{placedList.length > 1 ? 's' : ''} · {inr(placedTotal)}</b></span>
              <Icon name={showPlaced ? 'expand_less' : 'expand_more'} size={16} />
            </button>
          ) : <div className="px-2 text-[11px] text-arena-400 dark:text-white/40">Nothing placed on this session yet</div>}
          {showPlaced && placedList.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1 max-h-20 overflow-y-auto px-1">{placedList.map((p, i) => <span key={i} className="rounded-lg bg-arena-50 dark:bg-white/10 px-1.5 py-0.5 text-xs font-mono"><b>{p.number}</b> {inr(p.stake)}{p.byAgent ? ' · by agent' : ''}</span>)}</div>
          )}
        </div>

        {/* ── the only part that scrolls: entry controls on top, the slip below ── */}
        <div className="flex-1 min-h-0 overflow-y-auto px-2 pt-2 pb-3">
          {mode === 'quick' && (
            <>
              <div className="grid grid-cols-3 gap-1">
                {([['figure', `Single ×${market.multipliers.figure}`], ['jodi', `Jodi ×${market.multipliers.jodi}`], ['pana', 'Patti']] as const).map(([k, l]) => (
                  <button key={k} type="button" disabled={k === 'jodi' && !canJodi} onClick={() => { setType(k); setNum(''); setMsg(null); }} className={tab(type === k)}>{l}</button>))}
              </div>
              {type === 'jodi' && !canJodi && <div className="text-[11px] mt-1 text-arena-500">Jodi is played before the open result.</div>}
              {type === 'figure' ? (
                <div className="mt-2 grid grid-cols-5 gap-1.5">{DIGITS.map((d) => <button key={d} type="button" onClick={() => addQuick(d)} className={`h-11 text-lg ${chip}`}>{d}</button>)}</div>
              ) : (
                <div className="mt-2 flex gap-2">
                  <input ref={numRef} inputMode="numeric" pattern="[0-9]*" value={num} onChange={(e) => setNum(e.target.value.replace(/\D/g, '').slice(0, type === 'jodi' ? 2 : 3))} onKeyDown={(e) => { if (e.key === 'Enter') addQuick(num); }}
                    placeholder={type === 'jodi' ? '00 – 99' : '3-digit patti'} className="flex-1 h-11 rounded-xl px-3 font-mono font-black text-lg bg-arena-50 dark:bg-white/10 outline-none" />
                  <button type="button" onClick={() => addQuick(num)} className="h-11 px-4 rounded-xl bg-amber-600 text-white font-bold">Add</button>
                </div>
              )}
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <span className="text-xs font-bold text-arena-500 dark:text-white/60">Amount</span>
                <input inputMode="numeric" value={amt} onChange={(e) => setAmt(Number(e.target.value.replace(/\D/g, '')))} className="w-20 h-9 rounded-xl px-2 font-mono font-black bg-arena-50 dark:bg-white/10 outline-none" />
                {[10, 50, 100, 500].filter((p) => p >= rules.min && p <= rules.max).map((p) => <button key={p} type="button" onClick={() => setAmt(p)} className={`h-9 px-2.5 rounded-xl text-xs font-bold ${amt === p ? 'bg-amber-600 text-white' : 'bg-arena-100 dark:bg-white/10'}`}>₹{p}</button>)}
              </div>
            </>
          )}

          {mode === 'sheet' && (
            <div className="space-y-3">
              <div className="text-[11px] text-arena-500 dark:text-white/60">Type an amount under a number — it goes into the slip at once (the total is at the bottom). ✓ = already placed.</div>
              <div>
                <div className="text-[10px] uppercase font-bold text-arena-400">Single ×{market.multipliers.figure}</div>
                <div className="grid grid-cols-5 gap-1 mt-1">{DIGITS.map((d) => cell(d, false))}</div>
              </div>
              {canJodi && (
                <div>
                  <div className="text-[10px] uppercase font-bold text-arena-400">Jodi ×{market.multipliers.jodi}</div>
                  <div className="grid grid-cols-5 gap-1 mt-1">{Array.from({ length: 100 }, (_, i) => String(i).padStart(2, '0')).map((j) => cell(j, true))}</div>
                </div>
              )}
              <div>
                <div className="text-[10px] uppercase font-bold text-arena-400">Patti ×{market.multipliers.panaSingle}/{market.multipliers.panaDouble}/{market.multipliers.panaTriple} — grouped by the figure they make</div>
                {Object.keys(panaGroups).sort((a, b) => (a === '0' ? 1 : b === '0' ? -1 : Number(a) - Number(b))).map((d) => {
                  const inSlip = panaGroups[d].filter((p) => stakeOf(p)).length;
                  return (
                    <details key={d} className="mt-1" open={inSlip > 0 || undefined}>
                      <summary className="cursor-pointer text-xs font-bold">Figure {d} — {panaGroups[d].length} pattis{inSlip ? <span className="ml-1 text-amber-700 dark:text-amber-300">· {inSlip} in slip</span> : null}</summary>
                      <div className="grid grid-cols-5 gap-1 mt-1">{panaGroups[d].map((p) => cell(p, true))}</div>
                    </details>
                  );
                })}
              </div>
            </div>
          )}

          {mode === 'paste' && (
            <div className="space-y-2">
              <textarea value={pasteText} onChange={(e) => { setPasteText(e.target.value); setPreview(null); }} rows={5} placeholder={'128=50\n7 100\n47-20\n128,136,145=10'} className="w-full rounded-xl p-2 font-mono text-sm bg-arena-50 dark:bg-white/10 outline-none" />
              <div className="flex gap-2">
                <button type="button" onClick={doPreview} className="h-10 px-4 rounded-xl bg-arena-100 dark:bg-white/10 font-bold text-sm">Preview</button>
                {preview && !preview.rejected.length && <button type="button" onClick={addFromPreview} className="h-10 px-4 rounded-xl bg-amber-600 text-white font-bold text-sm">Add {preview.items.length} to the slip · {inr(preview.total)}</button>}
              </div>
              {preview && (
                <div className="rounded-xl bg-arena-50 dark:bg-white/5 p-2 text-xs space-y-1">
                  <div className="flex flex-wrap gap-1">{preview.items.map((it, i) => <span key={i} className="rounded bg-white dark:bg-white/10 px-1.5 py-0.5 font-mono"><b>{it.number}</b> {inr(it.stake)}</span>)}</div>
                  {preview.rejected.map((r, i) => <div key={i} className="text-red-600 dark:text-red-300">✗ "{r.raw}" — {r.reason}</div>)}
                  {preview.rejected.length > 0 && <div className="text-[11px] text-arena-500">Fix the red lines first — nothing is added until every line reads.</div>}
                </div>
              )}
            </div>
          )}

          {/* the slip — every tab */}
          {items.length > 0 && (
            <div className="mt-3 rounded-xl bg-arena-50 dark:bg-white/5 p-2">
              <div className="flex items-center justify-between text-[10px] uppercase font-bold text-arena-400">
                <span>In this slip — {items.length} number{items.length > 1 ? 's' : ''}</span>
                <button type="button" onClick={() => setItems([])} className="normal-case text-red-500 font-bold">Clear all</button>
              </div>
              {items.map((it, i) => (
                <div key={`${it.type}-${it.number}`} className="flex items-center gap-2 py-0.5 text-sm">
                  <span className="w-12 text-[10px] font-bold uppercase text-arena-500 dark:text-white/50">{TYPE_LABEL[it.type]}</span>
                  <span className="font-mono font-black">{it.number}</span>
                  <span className="flex-1 text-right font-mono">{inr(it.stake)}</span>
                  <button type="button" aria-label="Remove" onClick={() => setItems((xs) => xs.filter((_, j) => j !== i))} className="text-arena-400"><Icon name="close" size={16} /></button>
                </div>))}
              <div className="mt-1 pt-1 border-t border-arena-200 dark:border-white/10 flex items-center justify-between text-xs font-bold"><span>Total</span><span className="font-mono">{inr(total)}</span></div>
            </div>
          )}
        </div>

        {/* ── pinned footer: messages, the slip total, Place ── */}
        <div className="shrink-0 border-t border-arena-100 dark:border-white/10 bg-white dark:bg-[#24190b] px-3 pt-2" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 0.75rem)' }}>
          {msg && <div className="mb-1.5 text-xs rounded-lg bg-red-100 dark:bg-red-500/10 text-red-700 dark:text-red-300 px-2 py-1.5">{msg}</div>}
          {done && !msg && (
            <div className="mb-1.5 text-xs rounded-lg bg-green-100 dark:bg-green-500/15 text-green-800 dark:text-green-200 px-2 py-1.5 font-bold flex items-center gap-1">
              <Icon name="check_circle" size={16} /> Placed {done.count} number{done.count > 1 ? 's' : ''} · {inr(done.total)}. On this session now: {inr(placedTotal)}.
            </div>
          )}
          <div className="flex items-center gap-2">
            <div className="flex-1 min-w-0 text-[11px] leading-tight">
              {items.length ? (
                <>
                  <div className="font-bold">Slip: {items.length} number{items.length > 1 ? 's' : ''} · <span className="font-mono">{inr(total)}</span></div>
                  <div className="text-arena-500 dark:text-white/60 truncate">Single {inr(byType.figure)} · Jodi {inr(byType.jodi)} · Patti {inr(byType.pana)}</div>
                </>
              ) : <div className="text-arena-500 dark:text-white/60">Add numbers — they show here with the total, then press Place.</div>}
            </div>
            <button type="button" disabled={!items.length || busy} onClick={doSubmit} className="h-12 px-5 rounded-2xl bg-amber-600 text-white font-black text-base disabled:opacity-40 active:scale-[.99] shrink-0">
              {busy ? 'Placing…' : items.length ? `Place ${inr(total)}` : 'Place'}
            </button>
          </div>
          <div className="mt-1 text-[10px] text-center text-arena-400 dark:text-white/40">Bets are final once placed — you can add more, never reduce.</div>
        </div>
      </div>
    </div>
  );
}
